import cx from 'classnames'

import {
  type SanityLink,
  type SanityLinkFragment,
} from '@data/sanity/queries/types/link'
import { type SanityMenuFeaturedLinkFragment } from '@data/sanity/queries/types/site'
import { getLinkPageUrl } from '@lib/routes'
import { useUser } from '@lib/auth'

import AccountButton from '@components/buttons/account-button'
import LanguageSwitch from '@components/language-switch'
import CustomLink from '@components/link'
import Dropdown from './dropdown'
import DropdownButton from './dropdown-button'

export type ItemType = SanityLinkFragment | SanityMenuFeaturedLinkFragment

interface MenuItemProps {
  menuId?: string
  item: ItemType
  hasFocus: boolean
  useMegaNav: boolean
  onClick?: () => void
  isFooterMenuItem?: boolean
  isHeaderDesktopMenuItem?: boolean
  isHeaderMobilePrimaryMenuItem?: boolean
  isHeaderMobileSecondaryMenuItem?: boolean
  isMegaNavMenuItem?: boolean
}

const MenuItem = ({
  menuId,
  item,
  hasFocus,
  useMegaNav,
  onClick,
  isFooterMenuItem,
  isHeaderDesktopMenuItem,
  isHeaderMobilePrimaryMenuItem,
  isHeaderMobileSecondaryMenuItem,
  isMegaNavMenuItem,
}: MenuItemProps) => {
  const { user } = useUser()

  const title = 'title' in item && item.title ? item.title : ''
  const isButton = 'isButton' in item && item.isButton ? item.isButton : false

  const itemClassNames = cx(
    !isButton
      ? {
          'hover:opacity-60 transition-opacity duration-200 !no-underline':
            true,
          'opacity-50 hover:!opacity-100 transition-opacity duration-200 inline-flex text-xl text-left':
            isFooterMenuItem,
          'block font-medium text-base p-2': isHeaderDesktopMenuItem,
          'block p-0 text-4xl text-left py-1': isHeaderMobilePrimaryMenuItem,
          'block p-0 text-xl text-left': isHeaderMobileSecondaryMenuItem,
          'inline-block text-4xl leading-normal': isMegaNavMenuItem,
        }
      : {},
  )

  // Language switch
  if (item._type === 'navLanguageSwitch') {
    return (
      <LanguageSwitch
        id={item._key}
        buttonClassName={itemClassNames}
        hasCurrency={item.displayCurrency}
      />
    )
  }

  // Account button
  if (item._type === 'navAccountButton') {
    const accountUrl = getLinkPageUrl(
      user?.isLoggedIn ? 'accountPage' : 'loginPage',
    )

    return (
      <AccountButton
        id={`account-button-${item._key}`}
        className={cx(itemClassNames, { 'p-0': !item.text })}
        showIcon={item.displayIcon}
        href={accountUrl}
      >
        {item.text}
      </AccountButton>
    )
  }

  // Dropdown list
  if (item._type === 'navDropdown') {
    if (useMegaNav) {
      return (
        <DropdownButton
          id={item._key}
          title={title}
          className={itemClassNames}
        />
      )
    }

    if (!('dropdownItems' in item) || !item.dropdownItems) {
      return null
    }

    return (
      <Dropdown
        id={item._key}
        title={title}
        items={item.dropdownItems}
        onClick={onClick}
        listItemClassName={cx({
          'mx-2 my-0': isHeaderDesktopMenuItem,
        })}
        listItemLinkClassName={itemClassNames}
        buttonClassName={itemClassNames}
      />
    )
  }

  // Single link
  const titleSlug =
    'title' in item ? item.title.toLowerCase().replace(/ /g, '-') : ''
  const link: SanityLink = {
    ...item,
    ...(menuId && {
      id: `${menuId}-${titleSlug}`,
    }),
  }

  return (
    <CustomLink
      link={link}
      onClick={onClick}
      tabIndex={!hasFocus ? -1 : 0}
      className={itemClassNames}
      showCollectionCount={isMegaNavMenuItem}
    >
      {title}
    </CustomLink>
  )
}

export default MenuItem
